import React, { useRef, useState } from 'react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Camera, Trash2, Loader2 } from 'lucide-react';
import UserAvatar from '@/components/marketplace/UserAvatar';

const MAX_BYTES = 1 * 1024 * 1024;

/**
 * ProfilePhotoUploader — inline uploader used on the CompanyProfile page.
 *
 * Props:
 *  - user: current user (must have name; may have profile_photo / profile_photo_thumb / profile_photo_updated_at)
 *  - onChange: async fn called after upload/delete succeeds (typically refresh())
 */
export const ProfilePhotoUploader = ({ user, onChange }) => {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(false);

  const handlePick = () => fileRef.current?.click();

  const upload = async (file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please choose an image file (JPG, PNG, WebP).');
      return;
    }
    if (file.size > MAX_BYTES) {
      toast.error('Image too large. Max size is 1 MB.', {
        description: 'Tip: use a smaller photo or compress it before uploading.',
      });
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await api.post('/profile/photo', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Profile photo updated');
      onChange && (await onChange());
    } catch (e) {
      toast.error('Upload failed', { description: e?.response?.data?.detail || e.message });
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await api.delete('/profile/photo');
      toast.success('Profile photo removed');
      onChange && (await onChange());
    } catch (e) {
      toast.error('Delete failed', { description: e?.response?.data?.detail || e.message });
    } finally {
      setBusy(false);
      setPendingDelete(false);
    }
  };

  const hasPhoto = !!user?.profile_photo;

  return (
    <div className='flex items-start gap-4' data-testid='profile-photo-uploader'>
      <div className='relative'>
        <UserAvatar
          name={user?.name}
          photo={user?.profile_photo_thumb || user?.profile_photo}
          updatedAt={user?.profile_photo_updated_at}
          size={96}
          className='ring-2 ring-border'
          data-testid='profile-photo-avatar'
        />
        {busy && (
          <div className='absolute inset-0 flex items-center justify-center bg-background/70 rounded-full'>
            <Loader2 className='h-6 w-6 animate-spin text-primary' />
          </div>
        )}
      </div>
      <div className='flex-1 space-y-2'>
        <div className='text-sm font-medium'>Profile photo</div>
        <p className='text-xs text-muted-foreground'>
          Square PNG/JPG/WebP. Max 1&nbsp;MB. Auto-cropped & optimized.
        </p>
        <div className='flex flex-wrap gap-2'>
          <input
            ref={fileRef}
            type='file'
            accept='image/png,image/jpeg,image/webp'
            className='hidden'
            onChange={(e) => upload(e.target.files?.[0])}
            data-testid='profile-photo-file'
          />
          <Button
            size='sm'
            variant='outline'
            onClick={handlePick}
            disabled={busy}
            data-testid='profile-photo-upload-btn'
          >
            <Camera className='h-4 w-4 mr-1.5' />
            {hasPhoto ? 'Replace photo' : 'Upload photo'}
          </Button>
          {hasPhoto && (
            <Button
              size='sm'
              variant='ghost'
              className='text-destructive hover:text-destructive'
              onClick={() => setPendingDelete(true)}
              disabled={busy}
              data-testid='profile-photo-remove-btn'
            >
              <Trash2 className='h-4 w-4 mr-1.5' /> Remove
            </Button>
          )}
        </div>
        {pendingDelete && (
          <div className='mt-2 flex items-center gap-2 rounded-md border p-2 bg-muted/40 text-xs'>
            <span>Remove your profile photo? Others will see your initials instead.</span>
            <Button size='sm' variant='outline' onClick={() => setPendingDelete(false)} disabled={busy}>Cancel</Button>
            <Button size='sm' variant='destructive' onClick={remove} disabled={busy} data-testid='profile-photo-remove-confirm'>Remove</Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProfilePhotoUploader;
